import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { updateCustomLabels } from 'apis/cardApi';
import { EditOutlinedIcon, AddIcon } from 'icons/materialIcons';
import { useNavigate } from 'react-router-dom';
import { validateUUID } from 'utils/validator';
import { useParams } from 'react-router-dom';
import { useCardContext } from 'contexts/CardContext';
import { useAuth } from 'contexts/AuthContext';
import ChooseThemeField from './ChooseThemeField ';
import ChooseFontField from './ChooseFontField';
import ProfilePhotoCoverPhotoCompanyLogo from './profilePhotoCoverPhotoCompanyLogo/ProfilePhotoCoverPhotoCompanyLogo';
import { getCardLayouts, getCardById, updateCard, updateCardProfilePictureUrl, updateCardCompanyLogoUrl, updateCardCoverPhotoUrl } from 'apis/cardApi';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { getContactLinksByCard } from 'apis/contactLinkApi';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import PronounModal from './PronounModal';
import {
  Box, Grid, Typography, IconButton, Button, TextField, Dialog,
  DialogActions, DialogContent, DialogTitle, Tooltip, CircularProgress,
  InputAdornment
} from '@mui/material';

import { DeleteOutlineIcon } from 'icons/materialIcons';
import CustomLabelEditor from './CustomLabelEditor';

const AboutContainer = ({ user, setSelectedItem }) => {
  const initializedRef = useRef(false);
  const fetchedCardDetailsRef = useRef(false);
  const fetchedLayoutOptionsRef = useRef(false);
  const fetchedContactLinksRef = useRef(false);

  const [formState, setFormState] = useState({
    cardName: '',
    fullName: '',
    jobTitle: '',
    company: '',
    location: '',
    bio: '',
    theme: '',
    linkColor: '',
    cardLayout: ''
  });

  const { cardName, fullName, jobTitle, company, location, bio, theme, linkColor, cardLayout } = formState;
  const [loading, setLoading] = useState(false);
  const [isDeletingProfilePicture, setIsDeletingProfilePicture] = useState(false);
  const [isDeletingCoverPhoto, setIsDeletingCoverPhoto] = useState(false);
  const [isDeletingCompanyLogo, setIsDeletingCompanyLogo] = useState(false);
  const { authState } = useAuth();
  const [pronounModalOpen, setPronounModalOpen] = useState(false);
  const [pronoun, setPronoun] = useState('he');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showCharLimitNotification, setShowCharLimitNotification] = useState(false);

  const navigate = useNavigate();
  const [profilePictureBlob, setProfilePictureBlob] = useState(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState(null);
  const [companyLogoBlob, setCompanyLogoBlob] = useState(null);
  const [customLinkTypeLabels, setCustomLinkTypeLabelsState] = useState({
    'Contact': 'Contact',
    'Social': 'Social',
    'Products & Services': 'Products & Services'
  });

  const userId = authState.user.userId;
  const { setContactLinksForCard } = useContactLinkContext();

  const [openDialog, setOpenDialog] = useState(false);
  const { cardId: initialCardId } = useParams();
  const [cardId, setCardId] = useState(initialCardId);
  const [cardDetails, setCardDetails] = useState(null);
  const [dialogCardName, setDialogCardName] = useState('');
  const [cardLayoutOptions, setCardLayoutOptions] = useState([]);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [contactLinks, setContactLinks] = useState([]);
  const [cardDesign, setCardDesign] = useState('default');

  const {
    cardState,
    setCardFullName,
    setCardJobTitle,
    setCardCompany,
    setCardLocation,
    setCardTheme,
    setCardBio,
    setCardLinkColor,
    setCardProfilePictureUrl,
    setCardCoverPhotoUrl,
    setCardCompanyLogoUrl,
    setContextCardLayout,
    setCardName: contextSetCardName,
    updateCardData,
    setCardPronoun,
    setCustomLinkTypeLabels
  } = useCardContext();

  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const isCreationFlow = useMemo(() => validateUUID(cardId), [cardId]);
  const submitButtonText = isCreationFlow ? "Next" : "Update";

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const updateFormState = useCallback((updater) => {
    setFormState(prevState => {
      const newState = typeof updater === 'function' ? updater(prevState) : { ...prevState, ...updater };
      return newState;
    });
  }, []);

  useEffect(() => {
    if (bio.length < 250 && showCharLimitNotification) {
      setShowCharLimitNotification(false);
    }
  }, [bio.length, showCharLimitNotification]);





  // Add useEffect to fetch card design
  useEffect(() => {
    const fetchCardDesign = async () => {
      try {
        if (!validateUUID(cardId)) {
          const { success, card } = await getCardById(cardId);
          if (success && card) {
            setCardDesign(card.design || 'design5');
          }
        } else {
          setCardDesign('design5');
        }
      } catch (error) {
        setCardDesign('design5');
      }
    };

    if (cardId) {
      fetchCardDesign();
    }
  }, [cardId]);

  const fetchContactLinks = useCallback(async () => {
    if (!cardId || fetchedContactLinksRef.current) return;

    const isNewCardCreation = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    if (isNewCardCreation) {
      fetchedContactLinksRef.current = true;
      return;
    }

    try {
      const { success, contactLinks } = await getContactLinksByCard(cardId);
      if (success && Array.isArray(contactLinks)) {
        // Contact links loaded successfully
        setContactLinks(contactLinks);
        setContactLinksForCard(cardId, contactLinks);
      }
      fetchedContactLinksRef.current = true;
    } catch (error) {
      // Error fetching contact links
    }
  }, [cardId]);

  useEffect(() => {
    fetchContactLinks();
  }, [cardId]);

  useEffect(() => {
    const fetchLayoutOptions = async () => {
      if (fetchedLayoutOptionsRef.current || cardDesign === 'design5') return;

      try {
        const layouts = await getCardLayouts();
        setCardLayoutOptions(layouts);

        if (layouts.length > 0 && !formState.cardLayout) {
          const defaultLayout = layouts[0];
          updateFormState({ cardLayout: defaultLayout });
          setContextCardLayout(cardId, defaultLayout);
        }
        fetchedLayoutOptionsRef.current = true;
      } catch (error) {
        setCardLayoutOptions(['left-aligned', 'center-aligned', 'portrait']);

        if (!formState.cardLayout) {
          updateFormState({ cardLayout: 'left-aligned' });
          setContextCardLayout(cardId, 'left-aligned');
        }
      }
    };

    if (cardDesign !== 'design5') {
      fetchLayoutOptions();
    } else {
      if (!formState.cardLayout) {
        updateFormState({ cardLayout: 'left-aligned' });
        setContextCardLayout(cardId, 'left-aligned');
      }
    }
  }, [cardDesign, cardId]);

  useEffect(() => {
    if (isCreationFlow && !initializedRef.current) {
      const currentCardState = cardState[cardId];
      if (currentCardState) {
        const savedPronoun = currentCardState.cardPronoun || 'he';

        setFormState(prevState => ({
          ...prevState,
          cardName: currentCardState.cardName || '',
          fullName: currentCardState.cardFullName || '',
          pronoun: savedPronoun,
          jobTitle: currentCardState.cardJobTitle || '',
          company: currentCardState.cardCompany || '',
          location: currentCardState.cardLocation || '',
          bio: currentCardState.cardBio || '',
          theme: currentCardState.cardTheme || '',
          linkColor: currentCardState.cardLinkColor || '',
          cardLayout: currentCardState.contextCardLayout || 'left-aligned'
        }));

        setPronoun(savedPronoun);
        initializedRef.current = true;
        setDataInitialized(true);
      }
    }
  }, [cardId, isCreationFlow, cardState]);

  const fetchCardDetails = useCallback(async () => {
    if (fetchedCardDetailsRef.current || isCreationFlow || !cardId) return;

    try {
      setLoading(true);

      const data = await getCardById(cardId);
      setCardDetails(data);
      setLoading(false);
      fetchedCardDetailsRef.current = true;
    } catch (error) {
      setLoading(false);
    }
  }, [cardId, isCreationFlow]);

  useEffect(() => {
    if (!cardDetails?.card) {
      fetchCardDetails();
    }
  }, [cardId, isCreationFlow, cardDetails]);

  useEffect(() => {
    if (cardDetails?.card && !isCreationFlow && !initializedRef.current) {
      const savedPronoun = cardDetails.card.contact_info?.pronoun || 'he';

      setFormState(prev => ({
        ...prev,
        cardName: cardDetails.card.cardName || '',
        fullName: cardDetails.card.contact_info?.fullName || '',
        pronoun: savedPronoun,
        jobTitle: cardDetails.card.contact_info?.jobTitle || '',
        company: cardDetails.card.contact_info?.company || '',
        location: cardDetails.card.contact_info?.location || '',
        bio: cardDetails.card.bio || '',
        theme: cardDetails.card.cardTheme || '',
        linkColor: cardDetails.card.linkColor || '',
        cardLayout: cardDetails.card.cardLayout || 'left-aligned'
      }));

      setPronoun(savedPronoun);
      setProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
      initializedRef.current = true;
      setDataInitialized(true);
    }
  }, [cardDetails, isCreationFlow]);

  useEffect(() => {
    if (cardDetails?.card && !isCreationFlow) {
      const card = cardDetails.card;
      const savedPronoun = card.contact_info?.pronoun || 'he';

      try {
        setCardPronoun(cardId, savedPronoun);
        setContextCardLayout(cardId, card.cardLayout || 'left-aligned');
        setCardLocation(cardId, card.contact_info?.location || '');
        setCardBio(cardId, card.bio || '');
        setCardFullName(cardId, card.contact_info?.fullName || '');
        setCardJobTitle(cardId, card.contact_info?.jobTitle || '');
        setCardCompany(cardId, card.contact_info?.company || '');
        setCardTheme(cardId, card.cardTheme || '');
        setCardLinkColor(cardId, card.linkColor || '');
        setCardProfilePictureUrl(cardId, card.profilePictureUrl || '');
        setCardCoverPhotoUrl(cardId, card.coverPhotoUrl || '');
        setCardCompanyLogoUrl(cardId, card.companyLogoUrl || '');
        contextSetCardName(cardId, card.cardName || '');

        if (card.customLinkTypeLabels) {
          setCustomLinkTypeLabels(cardId, card.customLinkTypeLabels);
        }
      } catch (error) {
        // Error populating context
      }
    }
  }, [cardDetails, isCreationFlow, cardId]);

  const prevFormStateRef = useRef(null);

  useEffect(() => {
    prevFormStateRef.current = null;
  }, [cardId]);

  const handleInputChange = useCallback(async (event) => {
    const { name, value } = event.target;

    updateFormState({ [name]: value });

    try {
      switch (name) {
        case 'fullName':
          await setCardFullName(cardId, value);
          break;
        case 'jobTitle':
          await setCardJobTitle(cardId, value);
          break;
        case 'company':
          await setCardCompany(cardId, value);
          break;
        case 'location':
          await setCardLocation(cardId, value);
          break;
        case 'bio':
          await setCardBio(cardId, value);
          if (value.length === 250 && !showCharLimitNotification) {
            setShowCharLimitNotification(true);
            setTimeout(() => setShowCharLimitNotification(false), 3000);
          }
          break;
        default:
          break;
      }

      if (cardId) {
        const updateData = {};
        switch (name) {
          case 'fullName':
            updateData.cardFullName = value;
            break;
          case 'jobTitle':
            updateData.cardJobTitle = value;
            break;
          case 'company':
            updateData.cardCompany = value;
            break;
          case 'location':
            updateData.cardLocation = value;
            break;
          case 'bio':
            updateData.cardBio = value;
            break;
          default:
            break;
        }

        if (Object.keys(updateData).length > 0) {
          await updateCardData(cardId, updateData, false);
        }
      }
    } catch (error) {
      // Error updating context
    }
  }, [cardId, setCardBio, setCardCompany, setCardFullName, setCardJobTitle, setCardLocation, updateCardData, showCharLimitNotification]);

  const handleCardNameClick = useCallback(() => {
    setDialogCardName(cardName);
    setOpenDialog(true);
  }, [cardName]);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleCardNameChange = useCallback(() => {
    updateFormState({ cardName: dialogCardName });
    contextSetCardName(cardId, dialogCardName);
    setOpenDialog(false);
  }, [cardId, contextSetCardName, dialogCardName]);

  const handleOpenPronounModal = () => {
    setPronounModalOpen(true);
  };

  const handleClosePronounModal = () => {
    setPronounModalOpen(false);
  };

  const handleSavePronoun = (newPronoun) => {
    setPronoun(newPronoun);
    updateFormState({ pronoun: newPronoun });

    if (setCardPronoun) {
      setCardPronoun(cardId, newPronoun);
    }
  };

  const handleCardLayoutChange = useCallback((event) => {
    const newLayout = event.target.value;
    updateFormState({ cardLayout: newLayout });
    setContextCardLayout(cardId, newLayout);
  }, [cardId, setContextCardLayout]);

  const handleCroppedProfilePictureBlob = useCallback((blob) => {
    setProfilePictureBlob(blob);
  }, []);

  const handleCroppedCoverPhotoBlob = useCallback((blob) => {
    setCoverPhotoBlob(blob);
  }, []);

  const handleCroppedCompanyLogoBlob = useCallback((blob) => {
    setCompanyLogoBlob(blob);
  }, []);

  const handleProfilePictureUrlDelete = useCallback(() => {
    setIsDeletingProfilePicture(true);
    setProfilePictureBlob(null);
  }, []);

  const handleCoverPhotoUrlDelete = useCallback(() => {
    setIsDeletingCoverPhoto(true);
    setCoverPhotoBlob(null);
  }, []);

  const handleCompanyLogoUrlDelete = useCallback(() => {
    setIsDeletingCompanyLogo(true);
    setCompanyLogoBlob(null);
  }, []);

  const handleThemeChange = useCallback((newTheme) => {
    updateFormState({ theme: newTheme });
    setCardTheme(cardId, newTheme);
  }, [cardId, setCardTheme]);

  const handleLinkColorChange = useCallback((newLinkColor) => {
    updateFormState({ linkColor: newLinkColor });
    setCardLinkColor(cardId, newLinkColor);
  }, [cardId, setCardLinkColor]);

  const handleProfilePictureUrlSave = useCallback(async (cardId, url) => {
    try {
      await updateCardProfilePictureUrl(cardId, url);
    } catch (error) {
      // Error updating profile picture
    }
  }, []);

  const handleCoverPhotoUrlSave = useCallback(async (cardId, url) => {
    try {
      await updateCardCoverPhotoUrl(cardId, url);
    } catch (error) {
      // Error updating cover photo
    }
  }, []);

  const handleCompanyLogoUrlSave = useCallback(async (cardId, url) => {
    try {
      await updateCardCompanyLogoUrl(cardId, url);
    } catch (error) {
      // Error updating company logo
    }
  }, []);

  const handleCardUpdate = useCallback(async (cardId) => {
    const cardDetails = {
      contact_info: {
        fullName: fullName,
        jobTitle: jobTitle,
        company: company,
        location: location,
        pronoun: pronoun,
      },
      bio: bio,
      cardLayout: cardLayout,
      cardTheme: theme,
      cardName: cardName || '',
      linkColor: linkColor,
    };

    try {
      const result = await updateCard(cardId, cardDetails);
      return result;
    } catch (error) {
      throw error;
    }
  }, [bio, cardLayout, cardName, company, fullName, jobTitle, linkColor, location, theme, pronoun]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    const formData = {
      cardName,
      cardFullName: fullName,
      cardJobTitle: jobTitle,
      cardPronoun: pronoun,
      cardCompany: company,
      cardLocation: location,
      cardBio: bio,
      cardTheme: theme,
      cardLinkColor: linkColor,
      contextCardLayout: cardLayout,
      customLinkTypeLabels: customLinkTypeLabels,
    };

    updateCardData(cardId, formData);

    if (isCreationFlow) {
      navigate(`/dashboard/mycards/add-card/${cardId}?section=links`);

      if (setSelectedItem) {
        setSelectedItem('links');
      }

      return;
    }

    setLoading(true);

    try {
      if (cardDetails?.card?._id) {
        await handleCardUpdate(cardDetails.card._id);
        if (Object.keys(customLinkTypeLabels).length > 0) {
          await updateCustomLabels(cardDetails.card._id, customLinkTypeLabels);
        }

        if (coverPhotoBlob) {
          const newCoverPhotoUrl = await uploadImageBlob(
            coverPhotoBlob,
            setCoverPhotoUrl,
            'card',
            'cover',
            cardDetails.card._id
          );
          await handleCoverPhotoUrlSave(cardDetails.card._id, newCoverPhotoUrl);
        }

        if (!coverPhotoBlob && isDeletingCoverPhoto) {
          try {
            setCoverPhotoUrl(null);
            await deleteImage('card', 'cover', cardId);
            await updateCardCoverPhotoUrl(cardId, null);
          } catch (error) {
            // Error removing cover photo
          }
        }

        if (companyLogoBlob) {
          const newCompanyLogoUrl = await uploadImageBlob(
            companyLogoBlob,
            setCompanyLogoUrl,
            'card',
            'logo',
            cardDetails.card._id
          );
          await handleCompanyLogoUrlSave(cardDetails.card._id, newCompanyLogoUrl);
        }

        if (!companyLogoBlob && isDeletingCompanyLogo) {
          try {
            setCompanyLogoUrl(null);
            await deleteImage('card', 'logo', cardId);
            await updateCardCompanyLogoUrl(cardId, null);
          } catch (error) {
            // Error removing company logo
          }
        }

        if (profilePictureBlob) {
          const newProfilePictureUrl = await uploadImageBlob(
            profilePictureBlob,
            setProfilePictureUrl,
            'card',
            'profile',
            cardDetails.card._id
          );
          await handleProfilePictureUrlSave(cardDetails.card._id, newProfilePictureUrl);
        }

        if (!profilePictureBlob && isDeletingProfilePicture) {
          try {
            setProfilePictureUrl(null);
            await deleteImage('card', 'profile', cardId);
            await updateCardProfilePictureUrl(cardId, null);
          } catch (error) {
            // Error removing profile picture
          }
        }

        setSnackbarMessage('Card updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Failed to update card. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setIsDeletingProfilePicture(false);
      setIsDeletingCoverPhoto(false);
      setIsDeletingCompanyLogo(false);
    }
  }, [bio, cardId, cardLayout, cardName, company, companyLogoBlob, coverPhotoBlob,
    fullName, handleCardUpdate, handleCompanyLogoUrlSave, handleCoverPhotoUrlSave,
    handleProfilePictureUrlSave, isDeletingCompanyLogo, isDeletingCoverPhoto,
    isDeletingProfilePicture, isCreationFlow, jobTitle, linkColor, location,
    navigate, profilePictureBlob, setSelectedItem, theme, updateCardData, customLinkTypeLabels,
    pronoun]);

  const formatPronounDisplay = (pronoun) => {
    switch (pronoun) {
      case 'he': return 'he/him';
      case 'she': return 'she/her';
      case 'they': return 'they/them';
      case 'ze': return 'ze/zir';
      case '': return '';
      case 'custom': return 'custom';
      default:
        return pronoun;
    }
  };

  const defaultTabLabels = [
    { key: 'Contact', label: 'Name for contact links' },
    { key: 'Social', label: 'Name for social links' },
    { key: 'Products & Services', label: 'Name for products & services links' },
  ];

  const saveCustomLabelsRef = useRef(null);

  useEffect(() => {
    saveCustomLabelsRef.current = async (labels) => {
      try {
        await updateCustomLabels(cardId, labels);
      } catch (error) {
        setSnackbarMessage('Failed to save custom labels');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };
  }, [cardId]);

  const handleCustomLabelsChange = (newLabels) => {
    const completeLabels = {
      'Contact': newLabels['Contact'] !== undefined ? newLabels['Contact'] : 'Contact',
      'Social': newLabels['Social'] !== undefined ? newLabels['Social'] : 'Social',
      'Products & Services': newLabels['Products & Services'] !== undefined ? newLabels['Products & Services'] : 'Products & Services'
    };

    setCustomLinkTypeLabelsState(completeLabels);
    setCustomLinkTypeLabels(cardId, completeLabels);

    if (!isCreationFlow && saveCustomLabelsRef.current) {
      if (saveCustomLabelsRef.timeout) {
        clearTimeout(saveCustomLabelsRef.timeout);
      }
      saveCustomLabelsRef.timeout = setTimeout(() => {
        saveCustomLabelsRef.current(completeLabels);
      }, 500);
    }
  };

  return (
    <>
      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={handleSnackbarClose}
        severity={snackbarSeverity}
      />

      <NotificationSnackbar
        open={showCharLimitNotification}
        message="Character limit reached (max 250 characters)"
        handleClose={() => setShowCharLimitNotification(false)}
        severity="info"
        duration={3000}
      />

      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 2,
            backgroundColor: '#ffffff'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginBottom: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">About</Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f" }}>
                  Card Name:
                </Typography>
                <Button
                  variant="text"
                  onClick={handleCardNameClick}
                  value={cardName}
                  sx={{
                    textTransform: 'none',
                    padding: '4px 8px',
                    width: "150px",
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    backgroundColor: "#f7f7f7",
                    color: "#212121",
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                  }}
                  endIcon={<EditOutlinedIcon sx={{ color: '#828282', fontSize: "14px" }} />}
                >
                  {cardName || 'Card Title'}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {cardDesign !== 'design5' && (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f", display: 'flex', alignItems: 'center' }}>
                      Card Layout:
                    </Typography>
                    <TextField
                      select
                      SelectProps={{
                        native: true
                      }}
                      value={cardLayout}
                      onChange={handleCardLayoutChange}
                      sx={{
                        fontSize: '0.7rem',
                        minWidth: 120,
                        backgroundColor: "#f7f7f7",
                        color: "#212121",
                        fontWeight: 600,
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                      }}
                    >
                      {cardLayoutOptions.map((layout) => (
                        <option key={layout} value={layout}>
                          {layout}
                        </option>
                      ))}
                    </TextField>
                  </>
                )}
              </Box>
            </Box>

            <ProfilePhotoCoverPhotoCompanyLogo
              onCroppedProfilePictureBlob={handleCroppedProfilePictureBlob}
              onCroppedCoverPhotoBlob={handleCroppedCoverPhotoBlob}
              onCroppedCompanyLogoBlob={handleCroppedCompanyLogoBlob}
              onDeleteProfilePicture={handleProfilePictureUrlDelete}
              onDeleteCoverPhoto={handleCoverPhotoUrlDelete}
              onDeleteCompanylogo={handleCompanyLogoUrlDelete}
              cardLayout={cardLayout}
              cardDesign={cardDesign}
            />

            <Grid container spacing={2} sx={{ marginTop: 4 }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Name"
                  name="fullName"
                  value={fullName}
                  required
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                  Location
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Location"
                  name="location"
                  value={location}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px", marginTop: "5px" }}>
                  Job Title
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Job Title"
                  name="jobTitle"
                  value={jobTitle}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px", marginTop: "5px" }}>
                  Company
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Company"
                  name="company"
                  value={company}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ marginTop: 3 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                Bio
              </Typography>
              <TextField
                fullWidth
                name="bio"
                value={bio}
                onChange={handleInputChange}
                multiline
                minRows={2}
                maxRows={8}
                variant="outlined"
                inputProps={{
                  maxLength: 250
                }}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                    '& input::placeholder': {
                      fontSize: '0.8rem',
                      fontWeight: '300',
                    },
                  },
                }}
              />
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 1
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: bio.length >= 250 ? '#1976d2' : '#828282',
                    fontSize: '0.75rem',
                    fontWeight: bio.length >= 250 ? 600 : 400
                  }}
                >
                  {bio.length}/250 characters
                </Typography>
              </Box>
            </Box>

            <ChooseThemeField
              theme={theme}
              linkColor={linkColor}
              onThemeChange={handleThemeChange}
              onLinkColorChange={handleLinkColorChange}
            />

            {/* CustomLabelEditor for custom labels, no heading */}
            <Box sx={{ mt: 3 }}>
              <CustomLabelEditor
                labels={customLinkTypeLabels}
                onChange={handleCustomLabelsChange}
                defaultLabels={defaultTabLabels}
                loading={loading}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: 'auto' }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{
                backgroundColor: '#ffffff',
                borderColor: '#dedede',
                border: "0.75px solid",
                padding: '5px 16px',
                color: '#212121',
                textTransform: 'none',
                borderRadius: '10px',
                marginRight: "4px",
                '&:hover': {
                  backgroundColor: '#f7f7f7',
                  borderColor: '#dedede',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              type='submit'
              disabled={loading}
              sx={{
                backgroundColor: '#000000',
                padding: '5px 32px',
                color: '#ffffff',
                fontWeight: "bold",
                textTransform: 'none',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: '#333333',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
            </Button>
          </Box>

          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Enter Card Name</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Card Name"
                type="text"
                fullWidth
                variant="outlined"
                name="cardName"
                placeholder='Card Title'
                value={dialogCardName}
                onChange={(e) => setDialogCardName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleCardNameChange} color="primary" variant="contained">
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </form>
    </>
  );
};

export default React.memo(AboutContainer);